import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useAppSelector } from '@/app/store/hooks';
import { appQueryClient } from '@/shared/query';

/**
 * Provides the application's QueryClient, and keeps its cache to the session it was read in.
 *
 * - **Another user, or nobody:** the cache is cleared. One person's server data never draws on another's screen.
 * - **Another entity:** cached answers are reset and the visible ones are read again. Every request is scoped to the
 *   selected entity through `X-Entity-Id`, so a cached answer belongs to the previous entity.
 */
export function QueryProvider({ children }: { children: ReactNode }) {
  const userId = useAppSelector((state) => state.auth.user?.id ?? null);
  const entityId = useAppSelector((state) => state.selectedEntity.id);
  const seen = useRef({ userId, entityId });

  useEffect(() => {
    const before = seen.current;
    seen.current = { userId, entityId };
    if (before.userId !== userId) {
      appQueryClient.clear();
    } else if (before.entityId !== entityId) {
      void appQueryClient.resetQueries();
    }
  }, [userId, entityId]);

  return <QueryClientProvider client={appQueryClient}>{children}</QueryClientProvider>;
}
